import { Link, Head, router } from "@inertiajs/react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { lazy, Suspense } from "react";
import MobileNavigation from "@/Components/MobileNavigation";
import MetaTags from "@/Components/SEO/MetaTags";
import StructuredData from "@/Components/SEO/StructuredData";

const PricingCard = lazy(() => import("@/Components/PricingCard"));
const ExchangeMarquee = lazy(() => import("@/Components/ExchangeMarquee"));
const HowItWorksSection = lazy(
    () => import("@/Components/SEO/HowItWorksSection"),
);
const FAQSection = lazy(() => import("@/Components/SEO/FAQSection"));
const TargetAudienceSection = lazy(
    () => import("@/Components/SEO/TargetAudienceSection"),
);
import { MessageSquare, Sun, Moon } from "lucide-react";
import { User } from "@/types";

interface Exchange {
    id: number | string;
    name: string;
    display_name?: string;
    logo?: string;
    logo_url?: string;
}

interface Provider {
    id: string;
    resource_type: string;
    name: string;
    display_name: string;
    logo: string;
    auth_type: string;
    categories: string[];
    features: {
        account: boolean;
        positions: boolean;
        transactions: boolean;
        nfts: boolean;
        streaming: boolean;
    };
}

interface WelcomeProps {
    auth: {
        user: User;
    };
    laravelVersion: string;
    phpVersion: string;
    brazilianExchanges: {
        id: number;
        name: string;
        logo_url: string;
    }[];
    vezgoProviders: Provider[];
    vezgoBlockchainProviders: Provider[];
}

export default function Welcome({
    auth,
    brazilianExchanges,
    vezgoBlockchainProviders,
    vezgoProviders,
}: WelcomeProps) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const exchangesScrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [exchangeProviders, setExchangeProviders] = useState<Provider[]>([]);
    const [loadingProviders, setLoadingProviders] = useState(true);

    const checkScrollable = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } =
                scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
        }
    };

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", checkScrollable);
            checkScrollable();
        }

        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener("scroll", checkScrollable);
            }
        };
    }, []);

    useEffect(() => {
        const script = document.createElement("script");
        script.src =
            "https://scripts.converteai.net/8eb9394b-6032-4dd5-862c-f50d52b717c1/players/67f71b3036ab5ac5c4bf2e09/player.js";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    const handleSubscribe = (title: string) => {
        const whatsappNumber = "5511913341649";
        const message = `Quero contratar o plano ${title} da CryptoTax`;
        window.open(
            `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`,
            "_blank",
        );
    };

    useEffect(() => {
        const exchanges = [...vezgoProviders, ...vezgoBlockchainProviders]
            .filter(
                (provider) =>
                    (provider.categories.includes("exchange") ||
                        provider.categories.includes("blockchain") ||
                        provider.auth_type === "wallet") &&
                    provider.display_name !== "Vezgo Demo",
            )
            .sort((a, b) => {
                const nameA = (a.display_name || a.name).toLowerCase();
                const nameB = (b.display_name || b.name).toLowerCase();
                return nameA.localeCompare(nameB);
            })
            .slice(0, 50);

        setExchangeProviders(exchanges);
        setLoadingProviders(false);
    }, [vezgoProviders, vezgoBlockchainProviders]);

    const allExchanges: Exchange[] = [
        ...exchangeProviders.map((p) => ({
            id: p.id,
            name: p.name,
            display_name: p.display_name,
            logo: p.logo,
        })),
        ...brazilianExchanges.map((e) => ({
            id: e.id,
            name: e.name,
            logo_url: e.logo_url,
        })),
    ];

    return (
        <>
            <MetaTags
                title="Como Declarar Criptomoedas no Imposto de Renda 2025"
                description="Software completo para declarar Bitcoin e criptomoedas no IR. Integração com exchanges brasileiras (Mercado Bitcoin, Foxbit), carteiras DeFi, geração automática de IN-1888 e suporte de contadores especializados."
                keywords="como declarar criptomoedas no imposto de renda, declaração de imposto de renda criptomoedas, como declarar bitcoin no IR, declarar bitcoin receita federal, imposto de renda bitcoin 2025, software para declarar criptomoedas, declarar lucro com bitcoin, IN-1888 criptomoedas, DARF criptomoedas, exchanges brasileiras declaração IR"
                url="https://cryptotax.com.br"
            />
            <StructuredData
                type="SoftwareApplication"
                data={{
                    name: "CryptoTax",
                    price: "1997",
                    description:
                        "Software completo para declarar Bitcoin e criptomoedas no imposto de renda. Integração automática com exchanges brasileiras e internacionais, geração de IN-1888, cálculo de DARF e suporte de contadores especializados em criptoativos.",
                }}
            />
            <StructuredData type="Organization" data={{}} />
            <div className="overflow-x-hidden dark:bg-gray-900">
                <header
                    className="relative h-screen overflow-hidden"
                    role="banner"
                >
                    {/* Video Background with Green Overlay */}
                    <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
                        <div className="absolute inset-0 bg-[#52E747]/20 mix-blend-multiply z-10 dark:bg-[#52E747]/30"></div>
                        <img
                            src="/welcome-yatch.webp"
                            srcSet="/welcome-yatch.webp 1x"
                            alt="Como declarar criptomoedas no imposto de renda - Software completo para declaração de Bitcoin e criptoativos"
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ filter: "brightness(0.7) contrast(1.1)" }}
                            loading="eager"
                            fetchPriority="high"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50/30 dark:from-transparent dark:via-transparent dark:to-gray-900 z-20"></div>

                        {/* Scroll indicator arrow */}
                        <button
                            onClick={() => {
                                document
                                    .getElementById("pricing")
                                    ?.scrollIntoView({
                                        behavior: "smooth",
                                    });
                            }}
                            className="absolute bottom-8 left-0 right-0 flex justify-center z-30 cursor-pointer"
                        >
                            <div className="animate-bounce bg-white/20 backdrop-blur-sm p-2 w-10 h-10 ring-1 ring-white/20 shadow-lg rounded-full flex items-center justify-center">
                                <svg
                                    className="w-6 h-6 text-white"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                                </svg>
                            </div>
                        </button>
                    </div>

                    {/* Navigation Bar */}
                    <div className="relative z-30 py-4 md:py-6">
                        <div className="px-4 sm:px-14">
                            <div className="flex items-center justify-between">
                                <div className="flex-shrink-0">
                                    <Link
                                        href="#"
                                        className="flex rounded outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-2"
                                    >
                                        <img
                                            src="/logo-black.webp"
                                            className="w-32 h-24 sm:w-56 sm:h-30 md:h-32 hidden dark:block"
                                            alt="CryptoTax - Software para declarar criptomoedas no imposto de renda"
                                            loading="eager"
                                        />
                                        <img
                                            src="/logo-white.webp"
                                            className="w-32 h-24 sm:w-56 sm:h-30 md:h-32 block dark:hidden"
                                            alt="CryptoTax - Declaração de IR para Bitcoin e criptomoedas"
                                            loading="eager"
                                        />
                                    </Link>
                                </div>

                                <div className="flex items-center lg:hidden">
                                    <button
                                        type="button"
                                        className="text-white dark:text-white ml-2"
                                        onClick={() => setExpanded(!expanded)}
                                        aria-expanded={expanded}
                                    >
                                        <span
                                            className={
                                                expanded ? "hidden" : "block"
                                            }
                                            aria-hidden="true"
                                        >
                                            <svg
                                                className="w-7 h-7"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="1.5"
                                                    d="M4 6h16M4 12h16M4 18h16"
                                                />
                                            </svg>
                                        </span>

                                        <span
                                            className={
                                                expanded ? "block" : "hidden"
                                            }
                                            aria-hidden="true"
                                        >
                                            <svg
                                                className="w-7 h-7"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </span>
                                    </button>
                                </div>

                                <div className="hidden lg:ml-auto lg:flex lg:items-center lg:space-x-6">
                                    {auth.user ? (
                                        <>
                                            <div className="flex items-center gap-2 bg-gray-100/80 backdrop-blur-sm rounded-full px-4 py-2">
                                                <span className="text-sm font-medium text-gray-700">
                                                    Olá, {auth.user.name}
                                                </span>
                                                <span className="h-4 w-px bg-gray-300"></span>
                                                <Link
                                                    href={route("logout")}
                                                    method="post"
                                                    as="button"
                                                    className="text-sm font-medium text-red-600 hover:text-red-800 transition-all duration-200"
                                                >
                                                    Sair
                                                </Link>
                                            </div>
                                            <Link
                                                href="/transactions"
                                                className="inline-flex items-center justify-center px-6 py-3 text-base font-bold leading-7 text-white transition-all duration-200 bg-black/80 backdrop-blur-sm border border-transparent rounded-xl hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                                                role="button"
                                            >
                                                {t("access_report")}
                                            </Link>
                                        </>
                                    ) : (
                                        <>
                                            <Link
                                                href="/login"
                                                className="text-base font-medium text-white dark:text-white transition-all duration-200 rounded focus:outline-none hover:text-opacity-80 focus:ring-1 focus:ring-white focus:ring-offset-2"
                                            >
                                                {t("login")}
                                            </Link>
                                            <Link
                                                href="/register"
                                                className="inline-flex items-center justify-center px-6 py-3 text-base font-bold leading-7 text-white dark:text-white transition-all duration-200 bg-black/80 backdrop-blur-sm border border-transparent rounded-xl hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                                                role="button"
                                            >
                                                {t("register")}
                                            </Link>
                                        </>
                                    )}
                                </div>
                            </div>

                            <MobileNavigation
                                user={auth.user}
                                expanded={expanded}
                            />
                        </div>
                    </div>

                    {/* Hero Content */}
                    <div className="absolute inset-0 flex flex-col justify-center items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 relative z-30 pt-2">
                        <div className="max-w-2xl mx-auto text-center">
                            <h1 className="px-6 text-lg text-white font-inter font-semibold drop-shadow-md">
                                Declaração de Imposto de Renda para Investidores
                                de Criptomoedas
                            </h1>
                            <p className="mt-5 text-4xl font-bold leading-tight text-white sm:leading-tight sm:text-5xl lg:text-6xl lg:leading-tight font-pj drop-shadow-lg">
                                Declare suas{" "}
                                <span className="relative inline-flex sm:inline">
                                    <span className="bg-gradient-to-r from-[#52E747] via-[#44BCFF] to-[#52E747] blur-lg filter opacity-50 w-full h-full absolute inset-0"></span>
                                    <span className="relative text-white">
                                        Criptos
                                    </span>
                                </span>{" "}
                                sem Stress
                            </p>
                            <p className="mt-6 text-lg text-white/90 max-w-2xl mx-auto drop-shadow-md">
                                Software completo para declarar Bitcoin,
                                Ethereum e todas as suas criptomoedas.
                                Integração automática com exchanges brasileiras,
                                carteiras DeFi e suporte de contadores
                                especializados.
                            </p>

                            <div className="px-8 sm:items-center sm:justify-center sm:px-0 sm:space-x-5 sm:flex mt-9">
                                <Link
                                    href="/transactions"
                                    className="inline-flex items-center justify-center w-full px-8 py-3 text-lg font-bold text-white dark:text-white transition-all duration-200 bg-black/80 backdrop-blur-sm border-2 border-transparent sm:w-auto rounded-xl font-pj hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black shadow-lg"
                                    role="button"
                                >
                                    {t("access_report")}
                                </Link>
                                <a
                                    href="https://form.typeform.com/to/CWE83cDx"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center w-full px-8 py-3 mt-4 text-lg font-bold text-white dark:text-white transition-all duration-200 border-2 border-[#52E747] bg-[#52E747]/30 backdrop-blur-sm sm:w-auto sm:mt-0 rounded-xl font-pj focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#52E747] hover:bg-[#52E747] focus:bg-[#52E747] hover:text-white focus:text-white hover:border-[#52E747] focus:border-[#52E747] shadow-lg"
                                    role="button"
                                >
                                    Diagnóstico Gratuito
                                </a>
                            </div>
                        </div>
                    </div>
                </header>

                <section
                    className="py-16 bg-gray-50 dark:bg-gray-800 relative overflow-hidden"
                    aria-label="Exchanges suportadas"
                >
                    <div className="absolute left-0 bottom-0 w-40 h-40 bg-gradient-to-tr from-[#52E747] to-black opacity-10 rounded-full -translate-x-1/2 translate-y-1/4"></div>
                    <div className="absolute right-0 top-0 w-48 h-48 bg-gradient-to-bl from-[#52E747] to-black opacity-10 rounded-full translate-x-1/3 -translate-y-1/3"></div>

                    <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 relative z-10">
                        <div className="max-w-2xl mx-auto text-center mb-10">
                            <span className="inline-block px-4 py-1.5 bg-[#52E747]/10 text-[#52E747] text-sm font-medium rounded-full mb-4">
                                {t("exchanges")}
                            </span>
                            <h2 className="text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-5xl mb-2 dark:text-white">
                                Integração com Mais de 60 Exchanges e Carteiras
                            </h2>
                            <p className="text-gray-600 mt-4 max-w-lg mx-auto dark:text-white">
                                Conecte exchanges brasileiras (Mercado Bitcoin,
                                Foxbit), exchanges internacionais (Binance,
                                Coinbase), e carteiras nas redes Ethereum,
                                Solana, BSC e Polygon. Suporte completo para
                                DeFi, staking e NFTs na declaração de IR.
                            </p>
                        </div>

                        <div className="relative max-w-6xl mx-auto">
                            <div className="space-y-8">
                                <Suspense
                                    fallback={
                                        <div className="h-40 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                                    }
                                >
                                    <ExchangeMarquee
                                        exchanges={allExchanges.slice(
                                            0,
                                            Math.ceil(allExchanges.length / 2),
                                        )}
                                        direction="right"
                                        loading={loadingProviders}
                                    />
                                    <ExchangeMarquee
                                        exchanges={allExchanges.slice(
                                            Math.ceil(allExchanges.length / 2),
                                        )}
                                        direction="left"
                                        loading={loadingProviders}
                                    />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </section>

                <Suspense
                    fallback={
                        <div className="h-96 animate-pulse bg-gray-200 dark:bg-gray-700"></div>
                    }
                >
                    <HowItWorksSection />
                </Suspense>

                <Suspense
                    fallback={
                        <div className="h-96 animate-pulse bg-gray-200 dark:bg-gray-700"></div>
                    }
                >
                    <TargetAudienceSection />
                </Suspense>

                <Suspense
                    fallback={
                        <div className="h-96 animate-pulse bg-gray-200 dark:bg-gray-700"></div>
                    }
                >
                    <FAQSection />
                </Suspense>

                <section
                    id="pricing"
                    className="py-20 relative overflow-hidden bg-gray-50 dark:bg-gray-900"
                    aria-label="Planos e preços"
                >
                    {/* Background decorative elements */}
                    <div className="absolute left-0 top-0 w-32 h-32 bg-gradient-to-br from-[#52E747] to-black opacity-10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute right-0 bottom-0 w-64 h-64 bg-gradient-to-tl from-[#52E747] to-black opacity-10 rounded-full translate-x-1/4 translate-y-1/4"></div>

                    <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="max-w-2xl mx-auto text-center mb-8 sm:mb-12">
                            <span className="inline-block px-4 py-1.5 bg-[#52E747]/10 text-[#52E747] text-sm font-medium rounded-full mb-4">
                                {t("complete_plan")}
                            </span>
                            <h2 className="text-3xl font-bold leading-tight text-black sm:text-4xl lg:text-5xl mb-2 dark:text-white">
                                Planos para Declaração de Criptomoedas no
                                Imposto de Renda
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 mt-4">
                                Escolha o melhor plano para declarar Bitcoin e
                                suas criptomoedas. Suporte completo, IN-1888
                                automática e contadores especializados
                            </p>
                        </div>
                        <div className="-mx-4 px-4 overflow-x-auto sm:overflow-visible pb-4">
                            <div className="flex gap-4 sm:gap-8 sm:grid sm:grid-cols-2 sm:justify-center min-w-max sm:min-w-0 snap-x snap-mandatory">
                                <Suspense
                                    fallback={
                                        <div className="snap-start shrink-0 w-[85vw] max-w-sm sm:w-auto sm:max-w-none sm:shrink h-96 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                                    }
                                >
                                    <div className="snap-start shrink-0 w-[85vw] max-w-sm sm:w-auto sm:max-w-none sm:shrink">
                                        <div className="relative z-10 transform transition-transform duration-300 sm:hover:scale-105">
                                            <PricingCard
                                                title="Pro"
                                                price={1997}
                                                duration="1 ano"
                                                connections="Ilimitado corretoras e 2 carteiras"
                                                features={[
                                                    "tax_report_preparation",
                                                    "dedicated_support",
                                                    "in1888_exchanges",
                                                    "in1888_defi",
                                                    "monitoring_irregularities",
                                                ]}
                                                negativeFeatures={[
                                                    "accountant_responsible",
                                                ]}
                                                handleSubscribe={
                                                    handleSubscribe
                                                }
                                                subscribed={
                                                    auth.user?.has_access
                                                }
                                                variant="pro"
                                                recommended={false}
                                            />
                                        </div>
                                    </div>

                                    <div className="snap-start shrink-0 w-[85vw] max-w-sm sm:w-auto sm:max-w-none sm:shrink">
                                        <div className="relative z-10 transform transition-transform duration-300 sm:hover:scale-105">
                                            <PricingCard
                                                title="Unlimited"
                                                price={4197}
                                                duration="1 ano"
                                                connections="Ilimitado corretoras e carteiras"
                                                features={[
                                                    "tax_report_preparation",
                                                    "dedicated_support",
                                                    "in1888_exchanges",
                                                    "in1888_defi",
                                                    "monitoring_irregularities",
                                                    "accountant_responsible",
                                                ]}
                                                negativeFeatures={[]}
                                                handleSubscribe={
                                                    handleSubscribe
                                                }
                                                subscribed={
                                                    auth.user?.has_access
                                                }
                                                variant="premium"
                                            />
                                        </div>
                                    </div>
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </section>

                {/* WhatsApp Support Button - Only shown for authenticated users with access */}
                {auth.user && auth.user.has_access && (
                    <a
                        href="https://wa.me/5511913341649"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-3 flex items-center gap-2 shadow-lg transition-all z-50"
                    >
                        <MessageSquare className="h-5 w-5" />
                        <span className="font-medium">Suporte</span>
                    </a>
                )}
            </div>
        </>
    );
}
